import cn from 'classnames';
import { times } from 'lodash';
import { useState } from 'react';

import { QueryParams } from 'common/urls/QueryParams';
import handDrawnOutline from 'src/assets/svg/landing/hand_drawn_outline.svg';
import { Carousel, CarouselSlide } from 'src/shared/design-system/Carousel';
import { DynamicImage } from 'src/shared/design-system/DynamicImage';
import { match } from 'ts-pattern';
import { StringParam, useQueryParam } from 'use-query-params';
import { Container } from 'web-common/src/shared/design-system/components/Container';

const STEPS = [
  {
    title: 'SHEF: A SHARED PERSONAL CHEF',
    // eslint-disable-next-line max-len -- backoff eslint
    copy: 'Our talented, local shefs cook for a handful of households at a time — delivering the quality and care of a personal chef, at a fraction of the price.',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/step_one.png',
  },
  {
    title: 'WE HANDPICK THE BEST LOCAL SHEFS',
    copy: 'Our culinary team finds and vets the top personal chefs in your neighborhood for skill, quality, and taste.',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/step_two.png',
  },
  {
    title: 'CHOOSE FROM FLEXIBLE DELIVERY OPTIONS',
    // eslint-disable-next-line max-len -- backoff eslint
    copy: 'Receive weekly curated meals - starting at $54/week, or place a one-time order, always with the option to work with one or multiple shefs.',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/step_three.png',
  },
  {
    title: 'ENJOY LOCALLY PREPARED HOMEMADE MEALS',
    // eslint-disable-next-line max-len -- backoff eslint
    copy: 'Your shef preps and cooks for 5-10 families per day, so your meals always arrive fresh — never manufactured, stored, or shipped.',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/step_four-2.png',
  },
];
const STEPS_LP_ADS = [
  {
    title: 'SHEF: A SHARED PERSONAL CHEF',
    // eslint-disable-next-line max-len -- backoff eslint
    copy: 'Our talented, local shefs cook for a handful of households at a time — delivering the quality and care of a personal chef, at a fraction of the price.',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/step_one.png',
  },
  {
    title: 'WE HANDPICK THE BEST LOCAL SHEFS',
    copy: 'Our culinary team finds and vets the top personal chefs in your neighborhood for skill, quality, and taste.',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/step_two.png',
  },
  {
    title: 'CHOOSE FROM FLEXIBLE DELIVERY OPTIONS',
    // eslint-disable-next-line max-len -- backoff eslint
    copy: 'Receive weekly curated meals - starting at $72/week, or place a one-time order, always with the option to work with one or multiple shefs.',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/step_three.png',
  },
  {
    title: 'ENJOY LOCALLY PREPARED HOMEMADE MEALS',
    // eslint-disable-next-line max-len -- backoff eslint
    copy: 'Your shef preps and cooks for 5-10 families per day, so your meals always arrive fresh — never manufactured, stored, or shipped.',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/step_four-2.png',
  },
];

const IMAGE_WIDTH = 350;
const IMAGE_HEIGHT = 350;
const MOBILE_IMAGE_WIDTH = 200;
const MOBILE_IMAGE_HEIGHT = 200;

export const ProductStepsSection = () => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [variantName] = useQueryParam(QueryParams.LANDING_PAGE_VARIANT, StringParam);

  const StepIndicator = ({ goto }) => (
    <div className='z-10 mt-3 flex cursor-pointer flex-row items-center justify-center gap-2 text-xl'>
      {times(STEPS.length).map((idx) => (
        <div
          key={idx}
          className={cn(
            'mt-0 flex h-6 w-6 items-center justify-center bg-contain bg-center bg-no-repeat pt-1 text-xl lg:h-12 lg:w-12',
            'md:h-8 md:w-8',
            {
              'text-raspberry': currentStepIndex === idx,
              'text-eggplant': currentStepIndex !== idx,
            }
          )}
          onClick={() => {
            setCurrentStepIndex(idx);
            goto(idx);
          }}
          style={{ backgroundImage: currentStepIndex === idx ? `url(${handDrawnOutline})` : '' }}>
          {idx + 1}
        </div>
      ))}
    </div>
  );
  const stepsToUse = match(variantName)
    .with('04', () => STEPS_LP_ADS)
    .with('05', () => STEPS_LP_ADS)
    .with('06a', () => STEPS_LP_ADS)
    .with('06b', () => STEPS_LP_ADS)
    .with('07', () => STEPS_LP_ADS)
    .otherwise(() => STEPS);

  return (
    <>
      <div className='mb-20 md:mb-44'>
        <Carousel
          autoplay
          className='overflow-y-hidden'
          autoplayInterval={15000}
          onChange={({ index }) => setCurrentStepIndex(index)}
          clickableIndicatorComponent={StepIndicator}
          renderAll
          playOnVisible>
          {stepsToUse.map(({ title, copy, imageUrl }) => (
            <CarouselSlide key={copy}>
              <Container>
                <div className='flex h-fit w-full items-center justify-center gap-10'>
                  <div className='flex grow justify-end max-md:hidden md:min-w-[400px]'>
                    <DynamicImage
                      src={imageUrl}
                      alt={copy}
                      width={IMAGE_WIDTH}
                      height={IMAGE_HEIGHT}
                      sourceWidth={IMAGE_WIDTH}
                      sourceHeight={IMAGE_HEIGHT}
                    />
                  </div>
                  <div className='flex min-h-[300px] w-full flex-col justify-center gap-3 max-md:items-center md:pr-10'>
                    <div className='md:hidden'>
                      <DynamicImage
                        src={imageUrl}
                        alt={copy}
                        width={MOBILE_IMAGE_WIDTH}
                        height={MOBILE_IMAGE_HEIGHT}
                        sourceWidth={MOBILE_IMAGE_WIDTH}
                        sourceHeight={MOBILE_IMAGE_HEIGHT}
                      />
                    </div>
                    <p className='text-sm font-black text-eggplant max-md:hidden'>HOW IT WORKS</p>
                    <h3 className='mb-6 text-4xl font-black uppercase text-eggplant md:text-5xl'>{title}</h3>
                    <div className='h-fit lg:w-[70%]'>
                      <p className='text-left text-xl leading-snug text-eggplant md:text-2xl'>{copy}</p>
                    </div>
                  </div>
                </div>
              </Container>
            </CarouselSlide>
          ))}
        </Carousel>
      </div>
    </>
  );
};
